<template>
    <Dialog :open="!!apiToken" @update:open="(val) => !val && $emit('close')">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Update permissions</DialogTitle>
                <DialogDescription>
                    Choose which actions <span class="font-medium text-foreground">{{ apiToken.name }}</span> can perform.
                </DialogDescription>
            </DialogHeader>

            <div class="grid grid-cols-2 gap-3 mt-2">
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model="form.read" />
                    <span class="text-sm text-foreground">Read</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model="form.create" />
                    <span class="text-sm text-foreground">Create</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model="form.update" />
                    <span class="text-sm text-foreground">Update</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model="form.delete" />
                    <span class="text-sm text-foreground">Delete</span>
                </label>
            </div>

            <DialogFooter class="mt-4">
                <Button variant="outline" :disabled="loading" @click="$emit('close')">Cancel</Button>
                <Button :disabled="loading" @click="save">Save</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface IApiToken {
    id: number;
    name: string;
    abilities: string[] | null;
}

const props = defineProps({
    apiToken: {
        type: Object as PropType<IApiToken>,
        required: true,
    },
});

const emit = defineEmits<{
    close: [];
}>();

const has = (perm: string): boolean => {
    const a = props.apiToken.abilities;
    if (!a) return false;
    return Object.values(a).includes(perm) || Object.values(a).includes('*');
};

const form = ref({
    read: has('read'),
    create: has('create'),
    update: has('update'),
    delete: has('delete'),
});

const loading = ref(false);

const save = () => {
    loading.value = true;
    router.put(
        route('settings.api-tokens.update-permissions', props.apiToken.id),
        form.value,
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                toast.success('Permissions updated');
                router.reload({ only: ['tokens'], preserveScroll: true, preserveState: true });
                emit('close');
            },
            onError: (errors) => {
                const message = (Object.values(errors)[0] as string) || 'Failed to update permissions';
                toast.error(message);
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
